import React, { useState, useEffect } from 'react';
import { fetchVerseResources } from '../services/iqbibleVerseResources';

const BibleResources = ({ selectedVerse, selectedVerseData, currentBook, currentChapter }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedResource, setExpandedResource] = useState(null);

  // Restore fetchResources function
  const fetchResources = async () => {
    setLoading(true);
    try {
      const verseNum = selectedVerse?.verse || selectedVerse;
      const book = currentBook;
      const chapter = currentChapter;
      const data = await fetchVerseResources({
        bookName: book,
        chapter: chapter,
        verse: verseNum,
        translation: 'kjv',
      });
      const apiResources = [];
      if (data.commentary && data.commentary.trim()) {
        apiResources.push({
          id: 'commentary',
          title: "Gills Commentary",
          author: "John Gill",
          type: "Commentary",
          shortContent: data.commentary.slice(0, 200) + (data.commentary.length > 200 ? '...' : ''),
          fullContent: data.commentary,
          cover: "book-covers/matthew-henry.jpg",
          rating: 4.7,
          reviews: 1000,
          publisher: "Public Domain",
          pages: 0,
          year: 1763
        });
      }
      if (Array.isArray(data.crossReferences) && data.crossReferences.length > 0) {
        apiResources.push({
          id: 'crossrefs',
          title: "Cross References",
          author: "Scripture",
          type: "Cross References",
          shortContent: `Cross-references for this verse: ${data.crossReferences.length} found`,
          fullContent: data.crossReferences.map(ref => JSON.stringify(ref)).join('\n'),
          cover: "book-covers/treasury-scripture.jpg",
          rating: 4.9,
          reviews: 567,
          publisher: "Public Domain",
          pages: 0,
          year: 2025
        });
      }
      if (Array.isArray(data.wordStudies) && data.wordStudies.length > 0) {
        apiResources.push({
          id: 'wordstudies',
          title: "Word Studies (Original Text)",
          author: "IQBible",
          type: "Word Studies",
          shortContent: `Key words: ${data.wordStudies.map(w => w.word).filter(Boolean).join(', ').slice(0, 60)}...`,
          fullContent: data.wordStudies.map(w => `${w.word || ''} (${w.strongs || ''}): ${w.glossary || ''}`).join('\n'),
          cover: "book-covers/strongs-hebrew.jpg",
          rating: 4.7,
          reviews: 1834,
          publisher: "IQBible",
          pages: 0,
          year: 2025
        });
      }
      if (Array.isArray(data.translations) && data.translations.length > 0) {
        apiResources.push({
          id: 'translations',
          title: "Parallel Translations",
          author: "Multiple Translators",
          type: "Translations",
          shortContent: `Available translations: ${data.translations.length}`,
          fullContent: data.translations.map(t => `${t[0]?.versionName || t[0]?.versionAbbreviation || ''}: ${t[0]?.t || ''}`).join('\n'),
          cover: "book-covers/esv-study.jpg",
          rating: 4.8,
          reviews: 2156,
          publisher: "Various",
          pages: 0,
          year: 2025
        });
      }
      // Book Info
      if (data.bookInfo && data.bookInfo.title) {
        apiResources.push({
          id: 'bookinfo',
          title: `Book Info: ${data.bookInfo.title}`,
          author: data.bookInfo.author || 'Unknown',
          type: 'Book Info',
          shortContent: data.bookInfo.description ? data.bookInfo.description.slice(0, 200) + (data.bookInfo.description.length > 200 ? '...' : '') : 'Book information available.',
          fullContent: JSON.stringify(data.bookInfo, null, 2),
          cover: "book-covers/esv-study.jpg",
          rating: 4.6,
          reviews: 500,
          publisher: data.bookInfo.publisher || 'Unknown',
          pages: data.bookInfo.pages || 0,
          year: data.bookInfo.year || 2025
        });
      }
      // ...existing code...
      // ...existing code...
      // ...existing code...
      // Topics
      if (Array.isArray(data.topics) && data.topics.length > 0) {
        apiResources.push({
          id: 'topics',
          title: 'Topics',
          author: 'IQBible',
          type: 'Topics',
          shortContent: `Topics for this verse: ${data.topics.length}`,
          fullContent: data.topics.map(t => JSON.stringify(t)).join('\n'),
          cover: "book-covers/treasury-scripture.jpg",
          rating: 4.6,
          reviews: 120,
          publisher: 'IQBible',
          pages: 0,
          year: 2025
        });
      }
      // ...existing code...
      setResources(apiResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedVerse) {
      fetchResources();
    }
  }, [selectedVerse, currentBook, currentChapter]);

  const categories = [
    { name: 'All', count: resources.length },
    { name: 'Commentaries', count: resources.filter(r => r.type === 'Commentary').length },
    { name: 'Cross References', count: resources.filter(r => r.type === 'Cross References').length },
    { name: 'Book Info', count: resources.filter(r => r.type === 'Book Info').length },
  // ...existing code...
  // ...existing code...
  // ...existing code...
    { name: 'Topics', count: resources.filter(r => r.type === 'Topics').length },
    { name: 'Word Studies', count: resources.filter(r => r.type === 'Word Studies').length },
    { name: 'Translations', count: resources.filter(r => r.type === 'Translations').length },
  // ...existing code...
  ];

  const filteredResources = activeCategory === 'All' 
    ? resources 
    : resources.filter(r => r.type === activeCategory.slice(0, -1) || r.type === activeCategory);

  if (!selectedVerse) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
          Select a Verse for Detailed Resources
        </h3>
        <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
          Click on any verse number to see commentary, cross-references, and study materials from leading Bible scholars and resources.
        </p>
        <div style={{ marginTop: '24px', padding: '16px', background: '#f3f4f6', borderRadius: '8px' }}>
          <p style={{ color: '#374151', fontSize: '14px' }}>
            📚 Access to 6 major commentaries<br/>
            🔗 Comprehensive cross-references<br/>
            📖 Study notes and word analysis<br/>
            🌍 Multiple Bible translations
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <h3 className="resource-header">
          Resources for {currentBook} {currentChapter}:{selectedVerse?.verse || selectedVerse}
        </h3>
        <input
          type="text"
          placeholder="Search commentary, cross-references, or topics..."
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>

      {/* Category Buttons */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {categories.map(category => (
            <button
              key={category.name}
              onClick={() => setActiveCategory(category.name)}
              style={{
                padding: '6px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                background: activeCategory === category.name ? '#f3f4f6' : 'white',
                color: activeCategory === category.name ? '#374151' : '#6b7280',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontWeight: activeCategory === category.name ? '500' : '400'
              }}
            >
              {category.name} {category.count}
            </button>
          ))}
        </div>
      </div>

      {/* Resources List */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading resources...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredResources.map(resource => (
              <div
                key={resource.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  background: 'white',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  boxShadow: expandedResource === resource.id ? '0 8px 16px rgba(0,0,0,0.15)' : 'none'
                }}
                onClick={() => setExpandedResource(expandedResource === resource.id ? null : resource.id)}
              >
                <div style={{ display: 'flex', gap: '16px' }}>
                  {/* Book Cover */}
                  <div style={{ flexShrink: 0 }}>
                    <img
                      src={resource.cover}
                      alt={resource.title}
                      style={{
                        width: '80px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div
                      style={{
                        width: '80px',
                        height: '100px',
                        background: '#f3f4f6',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#6b7280',
                        textAlign: 'center'
                      }}
                    >
                      📖<br/>Book<br/>Cover
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f2937', margin: 0, lineHeight: '1.4' }}>
                        {resource.title}
                      </h4>
                      <span style={{ fontSize: '18px', color: '#6b7280', marginLeft: '8px' }}>
                        {expandedResource === resource.id ? '▲' : '▼'}
                      </span>
                    </div>
                    
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>
                      by {resource.author}
                    </p>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ color: '#fbbf24' }}>★</span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                          {resource.rating}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          ({resource.reviews.toLocaleString()} reviews)
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                      {resource.publisher} • {resource.year} • {resource.pages.toLocaleString()} pages
                    </div>
                    
                    <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
                      {expandedResource === resource.id ? resource.fullContent : resource.shortContent}
                    </p>
                    
                    {expandedResource !== resource.id && (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#3b82f6', cursor: 'pointer' }}>
                          📖 Click to read full commentary...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default BibleResources;

